// components/PropertiesTab.tsx - Complete Properties Tab Component
import React, { useState } from 'react';
import { Search, CreditCard, Calendar, MapPin, Phone, Building } from 'lucide-react';
import { Property, Area, UserRole } from '../types';
import { calculateTotal, getStatusColor, getStatus, getFrequencyColor, filterProperties } from '../utils';

interface PropertiesTabProps {
  properties: Property[];
  areas: Area[];
  userRole: UserRole;
  onPaymentClick: (property: Property) => void;
}

const PropertiesTab: React.FC<PropertiesTabProps> = ({ 
  properties, 
  areas, 
  userRole, 
  onPaymentClick 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredProperties = properties.filter(property => {
    // Search filter
    const matchesSearch = property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.plotNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.phone.includes(searchTerm) ||
                         property.address.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    let matchesStatus = true;
    if (filterStatus === 'current') matchesStatus = calculateTotal(property) <= property.monthlyRate;
    else if (filterStatus === 'due') matchesStatus = calculateTotal(property) > property.monthlyRate && property.penalty === 0;
    else if (filterStatus === 'overdue') matchesStatus = property.penalty > 0;
    
    // Category filter
    const matchesCategory = selectedCategory === 'all' || property.category === selectedCategory;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const filterCounts = {
    all: properties.length,
    current: properties.filter(p => calculateTotal(p) <= p.monthlyRate).length,
    due: properties.filter(p => calculateTotal(p) > p.monthlyRate && p.penalty === 0).length,
    overdue: properties.filter(p => p.penalty > 0).length
  };

  const categoryCounts = {
    all: properties.length,
    Residential: properties.filter(p => p.category === 'Residential').length,
    Commercial: properties.filter(p => p.category === 'Commercial').length
  };

  const totalDues = properties.reduce((sum, p) => sum + calculateTotal(p), 0);
  const totalPenalties = properties.reduce((sum, p) => sum + p.penalty, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <Building className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-blue-600">{properties.length}</span>
          </div>
          <div className="text-sm text-blue-700 font-medium">Total Properties</div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <CreditCard className="w-8 h-8 text-green-600" />
            <span className="text-2xl font-bold text-green-600">{filterCounts.current}</span>
          </div>
          <div className="text-sm text-green-700 font-medium">Current Payments</div>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="w-8 h-8 text-yellow-600" />
            <span className="text-2xl font-bold text-yellow-600">{filterCounts.due}</span>
          </div>
          <div className="text-sm text-yellow-700 font-medium">Payments Due</div>
        </div>
        
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <MapPin className="w-8 h-8 text-red-600" />
            <span className="text-2xl font-bold text-red-600">{filterCounts.overdue}</span>
          </div>
          <div className="text-sm text-red-700 font-medium">Overdue</div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Financial Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 mb-1">₹{totalDues.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Total Outstanding</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600 mb-1">₹{totalPenalties.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Total Penalties</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-1">
              ₹{properties.reduce((sum, p) => sum + p.monthlyRate, 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Monthly Revenue</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, plot number, phone, or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 pr-4 py-4 w-full border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm text-gray-900 placeholder-gray-500"
          />
        </div>

        {/* Status Filters */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm font-medium text-gray-600 self-center mr-2">Status:</span>
          {[
            { id: 'all', name: 'All', count: filterCounts.all },
            { id: 'current', name: 'Current', count: filterCounts.current },
            { id: 'due', name: 'Due', count: filterCounts.due },
            { id: 'overdue', name: 'Overdue', count: filterCounts.overdue }
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setFilterStatus(filter.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                filterStatus === filter.id
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
              }`}
            >
              {filter.name} ({filter.count})
            </button>
          ))}
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm font-medium text-gray-600 self-center mr-2">Category:</span>
          {[
            { id: 'all', name: 'All Categories', count: categoryCounts.all },
            { id: 'Residential', name: 'Residential', count: categoryCounts.Residential },
            { id: 'Commercial', name: 'Commercial', count: categoryCounts.Commercial }
          ].map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                selectedCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>
      </div>

      {/* Properties List */}
      <div className="space-y-4">
        {filteredProperties.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-3xl border border-gray-200">
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No properties found</h3>
            <p className="text-gray-500">Try adjusting your search terms or filters</p>
          </div>
        ) : (
          filteredProperties.map(property => {
            const area = areas.find(a => a.id === property.areaId);
            
            return (
              <div key={property.id} className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">{property.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        property.category === 'Residential' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {property.category}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Building className="w-4 h-4" />
                        <span>Plot {property.plotNo} • Road {property.roadNo}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span>{property.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <MapPin className="w-3 h-3" />
                        <span>{property.address}</span>
                      </div>
                      {userRole === 'admin' && area && (
                        <div className="flex items-center space-x-2 text-xs text-blue-600">
                          <MapPin className="w-3 h-3" />
                          <span>Area: {area.name} ({area.code})</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(property)}`}>
                      {getStatus(property)}
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getFrequencyColor(property.paymentFrequency)}`}>
                      {property.paymentFrequency}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <div className="text-xs text-gray-500 mb-1">Monthly Rate</div>
                    <div className="text-lg font-bold text-gray-900">₹{property.monthlyRate}</div>
                  </div>
                  <div className="bg-red-50 rounded-2xl p-4">
                    <div className="text-xs text-gray-500 mb-1">Total Due</div>
                    <div className="text-lg font-bold text-red-600">₹{calculateTotal(property)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="text-center p-3 bg-blue-50 rounded-xl">
                    <div className="text-xs text-gray-500">Base</div>
                    <div className="font-bold text-blue-600">₹{property.monthlyRate}</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-xl">
                    <div className="text-xs text-gray-500">Dues</div>
                    <div className="font-bold text-yellow-600">₹{property.dues}</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-xl">
                    <div className="text-xs text-gray-500">Penalty</div>
                    <div className="font-bold text-red-600">₹{property.penalty}</div>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Last: {property.lastPayment}</span>
                  </div>
                  <span className="text-xs text-gray-500">Next: {property.nextDue}</span>
                </div>

                <button
                  onClick={() => onPaymentClick(property)}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 rounded-2xl font-semibold text-lg flex items-center justify-center space-x-3 hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <CreditCard className="w-5 h-5" />
                  <span>Collect Payment</span>
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Results Summary */}
      {filteredProperties.length > 0 && (
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="text-center text-sm text-gray-600">
            Showing {filteredProperties.length} of {properties.length} properties
            {searchTerm && ` matching "${searchTerm}"`}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertiesTab;